/**
 * Расчеты для калькуляторов здоровья
 */

/**
 * Расчет индекса массы тела (ИМТ)
 * 
 * @param {number} weight - Вес в килограммах
 * @param {number} height - Рост в сантиметрах
 * @returns {number} Индекс массы тела
 */
export const calculateBMI = (weight, height) => {
  // Переводим рост из сантиметров в метры
  const heightInMeters = height / 100;
  return weight / (heightInMeters * heightInMeters);
};

/**
 * Получение категории ИМТ
 * 
 * @param {number} bmi - Индекс массы тела
 * @returns {Object} Категория ИМТ и рекомендации
 */
export const getBMICategory = (bmi) => {
  if (bmi < 16) {
    return {
      category: 'Выраженный дефицит массы тела',
      description: 'Значительное недоедание или медицинские проблемы',
      recommendation: 'Срочно обратитесь к врачу для проверки здоровья и разработки плана набора веса.'
    };
  } else if (bmi < 18.5) {
    return {
      category: 'Недостаточная масса тела',
      description: 'Ниже нормального диапазона',
      recommendation: 'Рассмотрите возможность увеличения калорийности питания и обратитесь к диетологу.'
    };
  } else if (bmi < 25) {
    return {
      category: 'Нормальная масса тела',
      description: 'Здоровый диапазон',
      recommendation: 'Поддерживайте текущий вес с помощью сбалансированного питания и регулярной физической активности.'
    };
  } else if (bmi < 30) {
    return {
      category: 'Избыточная масса тела',
      description: 'Предожирение',
      recommendation: 'Рассмотрите возможность небольшого снижения веса через здоровое питание и увеличение физической активности.'
    };
  } else if (bmi < 35) {
    return {
      category: 'Ожирение I степени',
      description: 'Умеренное ожирение',
      recommendation: 'Обратитесь к врачу для оценки рисков здоровья и разработки плана снижения веса.'
    };
  } else if (bmi < 40) {
    return {
      category: 'Ожирение II степени',
      description: 'Выраженное ожирение',
      recommendation: 'Настоятельно рекомендуется посетить врача для комплексной оценки здоровья и лечения.'
    };
  } else {
    return {
      category: 'Ожирение III степени',
      description: 'Морбидное ожирение',
      recommendation: 'Требуется срочное медицинское вмешательство для снижения рисков для здоровья.'
    };
  }
};

/**
 * Расчет идеального веса по разным формулам
 * 
 * @param {number} height - Рост в сантиметрах
 * @param {string} gender - Пол ('male' или 'female')
 * @param {string} formula - Формула для расчета ('broca', 'devine', 'robinson', 'miller', 'hamwi')
 * @returns {number} Идеальный вес в килограммах
 */
export const calculateIdealWeight = (height, gender, formula = 'devine') => {
  const isMale = gender === 'male';
  
  switch (formula) {
    // Формула Брока
    case 'broca':
      return isMale
        ? height - 100 - (height - 100) * 0.1
        : height - 100 - (height - 100) * 0.15;
    
    // Формула Девина
    case 'devine':
      return isMale
        ? 50 + 2.3 * ((height / 2.54) - 60)
        : 45.5 + 2.3 * ((height / 2.54) - 60);
    
    // Формула Робинсона
    case 'robinson':
      return isMale
        ? 52 + 1.9 * ((height / 2.54) - 60)
        : 49 + 1.7 * ((height / 2.54) - 60);
    
    // Формула Миллера
    case 'miller':
      return isMale
        ? 56.2 + 1.41 * ((height / 2.54) - 60)
        : 53.1 + 1.36 * ((height / 2.54) - 60);
    
    // Формула Хамви
    case 'hamwi':
      return isMale
        ? 48 + 2.7 * ((height / 2.54) - 60)
        : 45.5 + 2.2 * ((height / 2.54) - 60);
      
    default:
      return calculateIdealWeight(height, gender, 'devine');
  }
};

/**
 * Расчет дневной нормы калорий
 * 
 * @param {number} weight - Вес в килограммах
 * @param {number} height - Рост в сантиметрах
 * @param {number} age - Возраст в годах
 * @param {string} gender - Пол ('male' или 'female')
 * @param {string} activityLevel - Уровень активности ('sedentary', 'light', 'moderate', 'active', 'very_active')
 * @returns {number} Дневная норма калорий
 */
export const calculateCalories = (weight, height, age, gender, activityLevel) => {
  // Коэффициенты активности
  const activityMultipliers = {
    sedentary: 1.2,      // Сидячий образ жизни, минимум физической активности
    light: 1.375,        // Легкая активность (легкие упражнения/спорт 1-3 дня в неделю)
    moderate: 1.55,      // Умеренная активность (умеренные упражнения/спорт 3-5 дней в неделю)
    active: 1.725,       // Интенсивная активность (интенсивные упражнения/спорт 6-7 дней в неделю)
    very_active: 1.9     // Очень интенсивная активность (физический труд или 2x в день тренировки)
  };
  
  // Расчет базового метаболизма по формуле Миффлина-Сан Жеора
  let bmr;
  if (gender === 'male') {
    bmr = 10 * weight + 6.25 * height - 5 * age + 5;
  } else {
    bmr = 10 * weight + 6.25 * height - 5 * age - 161;
  }
  
  // Умножаем на коэффициент активности
  return Math.round(bmr * (activityMultipliers[activityLevel] || 1.2));
};

/**
 * Расчет целевых зон пульса
 * 
 * @param {number} age - Возраст в годах
 * @param {number} restingHeartRate - Пульс в покое (опционально)
 * @returns {Object} Зоны пульса
 */
export const calculateHeartRateZones = (age, restingHeartRate = null) => {
  // Максимальный пульс по формуле Танака
  const maxHeartRate = 208 - 0.7 * age;
  
  // Расчет резерва сердечного ритма (метод Карвонена)
  const getHeartRate = (percent) => {
    if (restingHeartRate !== null) {
      return Math.round(restingHeartRate + (maxHeartRate - restingHeartRate) * (percent / 100));
    }
    return Math.round(maxHeartRate * (percent / 100));
  };
  
  return {
    max: Math.round(maxHeartRate),
    zones: [
      {
        name: 'Очень легкая активность',
        description: 'Разминка, восстановление',
        min: getHeartRate(50),
        max: getHeartRate(60)
      },
      {
        name: 'Легкая активность',
        description: 'Сжигание жира, базовая выносливость',
        min: getHeartRate(60),
        max: getHeartRate(70)
      },
      {
        name: 'Умеренная активность',
        description: 'Аэробная тренировка, улучшение выносливости',
        min: getHeartRate(70),
        max: getHeartRate(80)
      },
      {
        name: 'Тяжелая активность',
        description: 'Анаэробная тренировка, повышение результативности',
        min: getHeartRate(80),
        max: getHeartRate(90)
      },
      {
        name: 'Максимальная активность',
        description: 'Максимальная нагрузка, только для тренированных',
        min: getHeartRate(90),
        max: getHeartRate(100)
      }
    ]
  };
};

// Дополняем файл src/utils/calculations/health.js новыми функциями для расчета процента жира

/**
 * Расчет процента жира в организме по методу ВМС США (Navy Method)
 * 
 * @param {string} gender - Пол ('male' или 'female')
 * @param {number} height - Рост в сантиметрах
 * @param {number} weight - Вес в килограммах
 * @param {number} neck - Окружность шеи в сантиметрах
 * @param {number} waist - Окружность талии в сантиметрах
 * @param {number} hip - Окружность бедер в сантиметрах (только для женщин)
 * @returns {number} Процент жира в организме
 */
export const calculateNavyBodyFat = (gender, height, weight, neck, waist, hip = 0) => {
  try {
    // Убедимся, что все параметры являются числами
    height = parseFloat(height);
    weight = parseFloat(weight);
    neck = parseFloat(neck);
    waist = parseFloat(waist);
    hip = parseFloat(hip);
    
    // Проверка на валидность входных данных
    if (isNaN(height) || isNaN(weight) || isNaN(neck) || isNaN(waist) || 
        (gender === 'female' && isNaN(hip))) {
      throw new Error('Некорректные входные данные для расчета процента жира');
    }
    
    // Расчет процента жира по методу ВМС США
    let bodyFatPercentage;
    
    if (gender === 'male') {
      // Формула для мужчин
      const logValue = 495 / (1.0324 - 0.19077 * Math.log10(waist - neck) + 0.15456 * Math.log10(height)) - 450;
      bodyFatPercentage = Math.max(Math.min(logValue, 50), 2); // Ограничиваем результат между 2% и 50%
    } else {
      // Формула для женщин
      const logValue = 495 / (1.29579 - 0.35004 * Math.log10(waist + hip - neck) + 0.22100 * Math.log10(height)) - 450;
      bodyFatPercentage = Math.max(Math.min(logValue, 50), 10); // Ограничиваем результат между 10% и 50%
    }
    
    return bodyFatPercentage;
  } catch (error) {
    console.error('Ошибка в расчете процента жира по методу ВМС:', error);
    return gender === 'male' ? 15 : 25; // Возвращаем средние значения в случае ошибки
  }
};

/**
 * Расчет процента жира в организме (обобщенная функция)
 * 
 * @param {string} gender - Пол ('male' или 'female')
 * @param {number} age - Возраст в годах
 * @param {number} height - Рост в сантиметрах
 * @param {number} weight - Вес в килограммах
 * @param {number} neck - Окружность шеи в сантиметрах
 * @param {number} waist - Окружность талии в сантиметрах
 * @param {number} hip - Окружность бедер в сантиметрах (только для женщин)
 * @returns {number} Процент жира в организме
 */
export const calculateBodyFatPercentage = (gender, age, height, weight, neck, waist, hip = 0) => {
  // По умолчанию используем метод ВМС США (Navy Method)
  return calculateNavyBodyFat(gender, height, weight, neck, waist, hip);
};

/**
 * Расчет состава тела на основе веса и процента жира
 * 
 * @param {number} weight - Вес в килограммах
 * @param {number} bodyFatPercentage - Процент жира в организме
 * @returns {Object} Состав тела (жировая и безжировая масса)
 */
export const calculateBodyComposition = (weight, bodyFatPercentage) => {
  try {
    weight = parseFloat(weight);
    bodyFatPercentage = parseFloat(bodyFatPercentage);
    
    if (isNaN(weight) || isNaN(bodyFatPercentage)) {
      throw new Error('Некорректные входные данные для расчета состава тела');
    }
    
    // Расчет жировой массы
    const fatMass = (weight * bodyFatPercentage) / 100;
    
    // Расчет безжировой массы (мышцы, кости, органы, вода и т.д.)
    const leanMass = weight - fatMass;
    
    return {
      fatMass,
      leanMass
    };
  } catch (error) {
    console.error('Ошибка в расчете состава тела:', error);
    return {
      fatMass: weight * 0.2,
      leanMass: weight * 0.8
    };
  }
};

/**
 * Расчет процента жира в организме по методу Джексона-Поллока
 * 
 * @param {string} gender - Пол ('male' или 'female')
 * @param {number} age - Возраст в годах
 * @param {Array} skinfolds - Массив значений толщины кожных складок в мм
 * @param {number} method - Тип метода (3 или 7 точек)
 * @returns {number} Процент жира в организме
 */
export const calculateJacksonPollockBodyFat = (gender, age, skinfolds, method = 3) => {
  try {
    // Убедимся, что параметры корректны
    age = parseInt(age);
    
    if (isNaN(age) || age <= 0 || (method !== 3 && method !== 7) || 
        !Array.isArray(skinfolds) || skinfolds.length < method) {
      throw new Error('Некорректные входные данные для расчета процента жира');
    }
    
    // Проверяем, чтобы все складки были числами
    for (let i = 0; i < method; i++) {
      if (isNaN(parseFloat(skinfolds[i]))) {
        throw new Error('Некорректные значения кожных складок');
      }
    }
    
    let sumOfSkinfolds = 0;
    if (method === 3) {
      // 3-точечный метод: грудь, живот, бедро
      sumOfSkinfolds = parseFloat(skinfolds[0]) + parseFloat(skinfolds[1]) + parseFloat(skinfolds[2]);
    } else {
      // 7-точечный метод: грудь, живот, бедро, трицепс, лопатка, надподвздошная, подмышечная
      for (let i = 0; i < 7; i++) {
        sumOfSkinfolds += parseFloat(skinfolds[i]);
      }
    }
    
    // Расчет плотности тела
    let bodyDensity;
    
    if (gender === 'male') {
      if (method === 3) {
        // Формула Джексона-Поллока для мужчин (3 точки)
        bodyDensity = 1.10938 - 0.0008267 * sumOfSkinfolds + 0.0000016 * Math.pow(sumOfSkinfolds, 2) - 0.0002574 * age;
      } else {
        // Формула Джексона-Поллока для мужчин (7 точек)
        bodyDensity = 1.112 - 0.00043499 * sumOfSkinfolds + 0.00000055 * Math.pow(sumOfSkinfolds, 2) - 0.00028826 * age;
      }
    } else {
      if (method === 3) {
        // Формула Джексона-Поллока для женщин (3 точки)
        bodyDensity = 1.0994921 - 0.0009929 * sumOfSkinfolds + 0.0000023 * Math.pow(sumOfSkinfolds, 2) - 0.0001392 * age;
      } else {
        // Формула Джексона-Поллока для женщин (7 точек)
        bodyDensity = 1.097 - 0.00046971 * sumOfSkinfolds + 0.00000056 * Math.pow(sumOfSkinfolds, 2) - 0.00012828 * age;
      }
    }
    
    // Формула Сири для расчета процента жира из плотности тела
    const bodyFatPercentage = (495 / bodyDensity) - 450;
    
    // Ограничиваем результат разумными пределами
    return Math.max(Math.min(bodyFatPercentage, 50), gender === 'male' ? 2 : 10);
  } catch (error) {
    console.error('Ошибка в расчете процента жира по методу Джексона-Поллока:', error);
    return gender === 'male' ? 15 : 25; // Возвращаем средние значения в случае ошибки
  }
};



